import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useApp } from '../../contexts/AppContext';
import CaseTypeSelect from '../../components/CaseTypeSelect';
import useHelpTarget from '../../hooks/useHelpTarget';

export default function SettingsTab({ caseId }) {
  const { caseData, dispatch } = useApp();
  const navigate = useNavigate();

  // ── Edit form state ──────────────────────────────────────
  const [form, setForm] = useState({
    case_name: caseData?.name || '',
    case_number: caseData?.case_number || '',
    case_type: caseData?.case_type || 'other',
    notes: caseData?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // ── Processing preferences ───────────────────────────────
  const [autoIndex, setAutoIndex] = useState(
    caseData?.settings?.auto_index ?? false
  );
  const [costLimit, setCostLimit] = useState(
    caseData?.settings?.upload_cost_limit_usd ?? ''
  );
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [prefsMsg, setPrefsMsg] = useState(null);

  // ── Delete states ────────────────────────────────────────
  const [confirmDeleteDocs, setConfirmDeleteDocs] = useState(false);
  const [deletingDocs, setDeletingDocs] = useState(false);
  const [deleteDocsResult, setDeleteDocsResult] = useState(null);

  const [confirmDeleteCase, setConfirmDeleteCase] = useState(false);
  const [deletingCase, setDeletingCase] = useState(false);

  // ── Help anchors ─────────────────────────────────────────
  // Right-click the row or hit ⌘/ while focused — opens the
  // relevant help article scoped. Zero visible UI added.
  const autoIndexHelpRef = useHelpTarget({
    slug: 'document-readiness-tiers',
    label: 'Auto-index on upload',
    question: 'How does Auto-index on upload work?',
  });
  const costLimitHelpRef = useHelpTarget({
    slug: 'cost-limits-and-budget',
    label: 'Upload cost guard',
    question: 'How does the upload cost limit work?',
  });

  // ── Form helpers ─────────────────────────────────────────
  function onChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setSaveMsg(null);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setSaveMsg(null);
    try {
      const updated = await api.patch(`/cases/${caseId}`, {
        case_name: form.case_name || undefined,
        case_number: form.case_number || undefined,
        case_type: form.case_type,
        notes: form.notes || undefined,
      });
      dispatch({ type: 'SET_CASE', id: caseId, data: updated });
      setSaveMsg({ ok: true, text: 'Saved' });
    } catch (err) {
      setSaveMsg({ ok: false, text: err.message });
    } finally {
      setSaving(false);
    }
  }

  // ── Auto-index toggle ───────────────────────────────────
  async function handleAutoIndexToggle(checked) {
    setAutoIndex(checked);
    setSavingPrefs(true);
    setPrefsMsg(null);
    try {
      const updated = await api.patch(`/cases/${caseId}`, {
        settings: { auto_index: checked },
      });
      dispatch({ type: 'SET_CASE', id: caseId, data: updated });
      setPrefsMsg({ ok: true, text: 'Saved' });
    } catch (err) {
      setAutoIndex(!checked); // revert on failure
      setPrefsMsg({ ok: false, text: err.message });
    } finally {
      setSavingPrefs(false);
    }
  }

  // ── Upload cost limit ───────────────────────────────────
  async function handleCostLimitSave() {
    setSavingPrefs(true);
    setPrefsMsg(null);
    const val = costLimit === '' ? null : parseFloat(costLimit);
    if (val !== null && (isNaN(val) || val <= 0)) {
      setPrefsMsg({ ok: false, text: 'Enter a positive dollar amount or leave blank to disable' });
      setSavingPrefs(false);
      return;
    }
    try {
      const updated = await api.patch(`/cases/${caseId}`, {
        settings: { upload_cost_limit_usd: val },
      });
      dispatch({ type: 'SET_CASE', id: caseId, data: updated });
      setPrefsMsg({ ok: true, text: 'Saved' });
    } catch (err) {
      setPrefsMsg({ ok: false, text: err.message });
    } finally {
      setSavingPrefs(false);
    }
  }

  // ── Delete all documents ─────────────────────────────────
  async function handleDeleteDocs() {
    setDeletingDocs(true);
    try {
      const res = await api.del(`/documents/case/${caseId}/all`);
      setDeleteDocsResult(res);
      dispatch({ type: 'SET_DOCUMENTS', docs: [] });
      setConfirmDeleteDocs(false);
    } catch (err) {
      setDeleteDocsResult({ error: err.message });
    } finally {
      setDeletingDocs(false);
    }
  }

  // ── Delete case ──────────────────────────────────────────
  async function handleDeleteCase() {
    setDeletingCase(true);
    try {
      await api.del(`/cases/${caseId}`);
      navigate('/cases', { replace: true });
    } catch (err) {
      setSaveMsg({ ok: false, text: 'Delete failed: ' + err.message });
      setDeletingCase(false);
      setConfirmDeleteCase(false);
    }
  }

  const docCount = caseData?.document_count || 0;

  return (
    <div className="st-settings">
      {/* ── Case Edit Form ───────────────────────────────── */}
      <section className="st-section">
        <h3>Case Details</h3>
        <form onSubmit={handleSave} className="st-form">
          <label className="st-field">
            <span>Case Name</span>
            <input
              name="case_name"
              value={form.case_name}
              onChange={onChange}
              maxLength={255}
              required
            />
          </label>

          <label className="st-field">
            <span>Case Number</span>
            <input
              name="case_number"
              value={form.case_number}
              onChange={onChange}
              maxLength={100}
            />
          </label>

          <label className="st-field">
            <span>Case Type</span>
            <CaseTypeSelect
              value={form.case_type}
              onChange={v => onChange({ target: { name: 'case_type', value: v } })}
            />
          </label>

          <label className="st-field">
            <span>Notes</span>
            <textarea
              name="notes"
              value={form.notes}
              onChange={onChange}
              rows={4}
            />
          </label>

          <div className="st-form-actions">
            <button type="submit" className="btn primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            {saveMsg && (
              <span className={saveMsg.ok ? 'st-msg-ok' : 'st-msg-err'}>{saveMsg.text}</span>
            )}
          </div>
        </form>
      </section>

      {/* ── Processing Preferences ───────────────────────── */}
      <section className="st-section">
        <h3>Upload Preferences</h3>
        <div className="st-pref-row" ref={autoIndexHelpRef}>
          <div className="st-pref-info">
            <strong>Auto-index on upload</strong>
            <p>
              Make every document searchable as soon as processing finishes.
              Adds about $0.001 per page to processing cost.
            </p>
          </div>
          <label className="st-toggle">
            <input
              type="checkbox"
              checked={autoIndex}
              disabled={savingPrefs}
              onChange={(e) => handleAutoIndexToggle(e.target.checked)}
            />
            <span className="st-toggle-slider" />
          </label>
        </div>
        <div className="st-pref-row" style={{ marginTop: '16px' }} ref={costLimitHelpRef}>
          <div className="st-pref-info">
            <strong>Upload cost guard</strong>
            <p>
              Auto-pause uploads when estimated running cost reaches this limit.
              You can continue or cancel when the limit is reached.
              Leave blank to disable.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#666' }}>$</span>
            <input
              type="number"
              min="0"
              step="1"
              style={{ width: '90px', padding: '6px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
              value={costLimit}
              onChange={(e) => { setCostLimit(e.target.value); setPrefsMsg(null); }}
              disabled={savingPrefs}
              placeholder="—"
            />
            <button className="btn" onClick={handleCostLimitSave} disabled={savingPrefs}>
              {savingPrefs ? 'Saving…' : 'Set'}
            </button>
          </div>
        </div>
        {prefsMsg && (
          <span className={prefsMsg.ok ? 'st-msg-ok' : 'st-msg-err'}>{prefsMsg.text}</span>
        )}
      </section>

      {/* ── Danger Zone ──────────────────────────────────── */}
      <section className="st-section st-danger-zone">
        <h3>Danger Zone</h3>

        {/* Delete all documents */}
        <div className="st-danger-row">
          <div>
            <strong>Delete All Documents</strong>
            <p>Remove all {docCount} document{docCount !== 1 ? 's' : ''} from this case. This cannot be undone.</p>
          </div>
          {!confirmDeleteDocs ? (
            <button
              className="btn danger"
              onClick={() => setConfirmDeleteDocs(true)}
              disabled={docCount === 0}
            >
              Delete Documents
            </button>
          ) : (
            <div className="st-confirm">
              <span>Are you sure? This deletes {docCount} document{docCount !== 1 ? 's' : ''}.</span>
              <button className="btn danger" onClick={handleDeleteDocs} disabled={deletingDocs}>
                {deletingDocs ? 'Deleting…' : 'Yes, Delete All'}
              </button>
              <button className="btn" onClick={() => setConfirmDeleteDocs(false)} disabled={deletingDocs}>
                Cancel
              </button>
            </div>
          )}
          {deleteDocsResult && !deleteDocsResult.error && (
            <span className="st-msg-ok">Deleted {deleteDocsResult.deleted_count} documents</span>
          )}
          {deleteDocsResult?.error && (
            <span className="st-msg-err">{deleteDocsResult.error}</span>
          )}
        </div>

        {/* Delete case — not offered on a case shared with you as a consultant */}
        {caseData?.access !== 'consultant' && (
        <div className="st-danger-row">
          <div>
            <strong>Delete Case</strong>
            <p>Permanently delete this case and all associated data.</p>
          </div>
          {!confirmDeleteCase ? (
            <button className="btn danger" onClick={() => setConfirmDeleteCase(true)}>
              Delete Case
            </button>
          ) : (
            <div className="st-confirm">
              <span>This is permanent. Type the case name to confirm is not required — just confirm.</span>
              <button className="btn danger" onClick={handleDeleteCase} disabled={deletingCase}>
                {deletingCase ? 'Deleting…' : 'Yes, Delete Case'}
              </button>
              <button className="btn" onClick={() => setConfirmDeleteCase(false)} disabled={deletingCase}>
                Cancel
              </button>
            </div>
          )}
        </div>
        )}
      </section>
    </div>
  );
}
